import React from 'react';
import {
  Upload, FileText, ChevronLeft, CheckCircle, AlertCircle,
  X, ClipboardPaste, Home, Building2, RotateCcw,
  ArrowRight, Shield, Lock, FileUp,
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { updateServiceOrder, type ServiceType } from '../services/serviceOrderService';
import { performTenancyReviewAndRewrite, type TenancyReviewResult, type TenancyAnalysis } from '../services/tenancyReviewRewrite';
import { performCommercialLeaseReview, type CommercialLeaseDigest, type CommercialLeaseAnalysis } from '../services/commercialLeaseReview';
import type { Annotation } from '../services/tenancyReviewRewrite';
import type { ReviewProgress } from '../services/edgeFunctionClient';
import { ReviewProcessingLoader } from './ReviewProcessingLoader';

if (typeof window !== 'undefined' && 'Worker' in window) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url
    ).toString();
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
  }
}

interface PaidDocumentUploadProps {
  serviceType: ServiceType;
  orderId: string;
  onBack: () => void;
  hideBackButton?: boolean;
  onComplete: (result: {
    rewrittenDocument: string;
    annotations: Annotation[];
    analysis: Record<string, unknown>;
    originalText: string;
    negotiationSummary?: string;
  }) => void;
}

type Phase = 'upload' | 'processing' | 'error';

const SERVICE_INFO: Record<ServiceType, {
  title: string;
  icon: React.ReactNode;
  gradient: string;
  checklist: string[];
  heroImage: string;
  heroTagline: string;
  heroSubline: string;
  overlayGradient: string;
}> = {
  tenancy_review_rewrite: {
    title: 'Tenancy Agreement Review & Re-Write',
    icon: <Home className="w-5 h-5 text-white" />,
    gradient: 'from-gray-900 to-gray-800',
    checklist: [
      'Check against Renters\' Rights Bill 2025',
      'Remove Section 21 no-fault eviction clauses',
      'Convert to periodic tenancy format',
      'Add Decent Homes Standard provisions',
      'Add Awaab\'s Law damp/mould timescales',
      'Annotate every change with legal references',
    ],
    heroImage: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
    heroTagline: 'Your tenancy agreement, made compliant',
    heroSubline: 'Fully aligned with the Renters\' Rights Bill 2025',
    overlayGradient: 'from-black/80 via-gray-900/40 to-gray-800/60',
  },
  commercial_lease_review: {
    title: 'Commercial Lease Review',
    icon: <Building2 className="w-5 h-5 text-white" />,
    gradient: 'from-gray-900 to-gray-800',
    checklist: [
      'Read and compare both documents in full',
      'Translate legal jargon into plain English',
      'Identify hidden costs, obligations, and onerous terms',
      'Flag clauses that could restrict your business',
      'Provide clause-by-clause redline recommendations',
      'Assess negotiation leverage and suggest strategy',
    ],
    heroImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    heroTagline: 'Expert commercial lease analysis',
    heroSubline: 'Clause-by-clause review with negotiation strategy',
    overlayGradient: 'from-black/80 via-gray-900/40 to-gray-800/60',
  },
};

const STEPS_TENANCY = [
  { label: 'Payment', status: 'complete' as const },
  { label: 'Upload Document', status: 'active' as const },
  { label: 'Review Results', status: 'upcoming' as const },
];

const STEPS_COMMERCIAL = [
  { label: 'Payment', status: 'complete' as const },
  { label: 'Upload Documents', status: 'active' as const },
  { label: 'Review Results', status: 'upcoming' as const },
];

export function PaidDocumentUpload({ serviceType, orderId, onBack, hideBackButton, onComplete }: PaidDocumentUploadProps) {
  const [phase, setPhase] = React.useState<Phase>('upload');
  const [documentText, setDocumentText] = React.useState('');
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [inputMode, setInputMode] = React.useState<'file' | 'paste'>('file');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isDragging, setIsDragging] = React.useState(false);
  const [reviewProgress, setReviewProgress] = React.useState<ReviewProgress | null>(null);
  const [completedAnalysis, setCompletedAnalysis] = React.useState<TenancyAnalysis | CommercialLeaseAnalysis | null>(null);
  const [failedStage, setFailedStage] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const [documentTextB, setDocumentTextB] = React.useState('');
  const [fileNameB, setFileNameB] = React.useState<string | null>(null);
  const [inputModeB, setInputModeB] = React.useState<'file' | 'paste'>('file');
  const [isDraggingB, setIsDraggingB] = React.useState(false);
  const fileInputRefB = React.useRef<HTMLInputElement>(null);

  const info = SERVICE_INFO[serviceType];
  const isTenancy = serviceType === 'tenancy_review_rewrite';
  const isCommercial = serviceType === 'commercial_lease_review';
  const STEPS = isTenancy ? STEPS_TENANCY : STEPS_COMMERCIAL;

  React.useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const items = content.items as Array<{ str: string; transform: number[]; height: number }>;
      if (items.length === 0) continue;

      const lines: string[] = [];
      let currentLine = items[0].str;
      let lastY = items[0].transform[5];
      let lastHeight = items[0].height || 12;

      for (let j = 1; j < items.length; j++) {
        const item = items[j];
        const y = item.transform[5];
        const yDelta = Math.abs(y - lastY);

        if (yDelta > lastHeight * 0.5) {
          lines.push(currentLine);
          if (yDelta > lastHeight * 1.8) {
            lines.push('');
          }
          currentLine = item.str;
        } else {
          const endsWithSpace = currentLine.endsWith(' ') || item.str.startsWith(' ');
          currentLine += (endsWithSpace ? '' : ' ') + item.str;
        }
        lastY = y;
        lastHeight = item.height || lastHeight;
      }
      if (currentLine) lines.push(currentLine);
      pages.push(lines.join('\n'));
    }
    return pages.join('\n\n');
  };

  const extractTextFromDOCX = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  };

  const handleFile = async (file: File, target: 'A' | 'B' = 'A') => {
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrorMessage('File too large. Maximum size is 10MB.');
      setPhase('error');
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'txt', 'doc', 'docx'].includes(ext || '')) {
      setErrorMessage('Unsupported file type. Please upload a PDF, DOC, DOCX, or TXT file.');
      setPhase('error');
      return;
    }

    try {
      let text = '';
      if (ext === 'pdf') {
        text = await extractTextFromPDF(file);
      } else if (ext === 'doc' || ext === 'docx') {
        text = await extractTextFromDOCX(file);
      } else {
        text = await file.text();
      }

      if (!text.trim()) {
        setErrorMessage('Could not extract text from the file. Please try pasting the text directly.');
        setPhase('error');
        return;
      }

      if (target === 'B') {
        setDocumentTextB(text);
        setFileNameB(file.name);
      } else {
        setDocumentText(text);
        setFileName(file.name);
      }
    } catch {
      const isDoc = ext === 'doc';
      setErrorMessage(
        isDoc
          ? 'Could not read this .doc file. Please save it as .docx or .pdf and try again, or paste the text directly.'
          : 'Failed to read the file. Please try pasting the text instead.'
      );
      setPhase('error');
    }
  };

  const handleDrop = (e: React.DragEvent, target: 'A' | 'B' = 'A') => {
    e.preventDefault();
    if (target === 'B') {
      setIsDraggingB(false);
    } else {
      setIsDragging(false);
    }
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file, target);
  };

  const canProceed = isCommercial
    ? documentText.trim().length > 0 && documentTextB.trim().length > 0
    : documentText.trim().length > 0;

  const handleProceed = async (retryAnalysis?: TenancyAnalysis | CommercialLeaseAnalysis) => {
    if (!canProceed) return;

    const abortController = new AbortController();
    abortRef.current = abortController;

    setPhase('processing');
    setReviewProgress(null);
    setFailedStage(null);

    try {
      try {
        await updateServiceOrder(orderId, {
          original_document_text: documentText,
          original_filename: fileName || 'pasted-text.txt',
          status: 'processing',
        });
      } catch (dbErr) {
        console.warn('Failed to update service order status, continuing with analysis:', dbErr);
      }

      let result: { rewrittenDocument: string; annotations: Annotation[]; analysis: Record<string, unknown>; negotiationSummary?: string };
      let capturedAnalysis: TenancyAnalysis | CommercialLeaseAnalysis | null = null;

      const onProgress = (p: ReviewProgress) => {
        setReviewProgress({ ...p });
      };

      if (serviceType === 'tenancy_review_rewrite') {
        const tenancyResult: TenancyReviewResult = await performTenancyReviewAndRewrite(documentText, {
          onProgress,
          previousAnalysis: retryAnalysis as TenancyAnalysis | undefined,
          signal: abortController.signal,
        });
        capturedAnalysis = tenancyResult.analysis;
        result = {
          rewrittenDocument: tenancyResult.rewrittenDocument,
          annotations: tenancyResult.annotations,
          analysis: tenancyResult.analysis as unknown as Record<string, unknown>,
        };
      } else {
        const commercialResult: CommercialLeaseDigest = await performCommercialLeaseReview(documentText, documentTextB, {
          onProgress,
          previousAnalysis: retryAnalysis as CommercialLeaseAnalysis | undefined,
          signal: abortController.signal,
        });
        capturedAnalysis = commercialResult.analysis;
        result = {
          rewrittenDocument: commercialResult.documentB,
          annotations: commercialResult.annotations,
          analysis: commercialResult.analysis as unknown as Record<string, unknown>,
          negotiationSummary: commercialResult.negotiationSummary,
        };
      }

      setCompletedAnalysis(capturedAnalysis);

      try {
        await updateServiceOrder(orderId, {
          analysis_result: result.analysis,
          rewritten_document: result.rewrittenDocument,
          annotations: result.annotations,
          status: 'completed',
          completed_at: new Date().toISOString(),
        });
      } catch (dbErr) {
        console.warn('Failed to save results to service order, continuing:', dbErr);
      }

      onComplete({
        ...result,
        originalText: documentText,
      });
    } catch (err) {
      if (abortController.signal.aborted) return;
      console.error('Processing error:', err);

      const currentProgress = reviewProgress;
      if (currentProgress && currentProgress.stage > 1) {
        setCompletedAnalysis(completedAnalysis);
      }

      const stageNames: Record<string, string> = {
        analysing: 'analysis',
        rewriting: 'rewrite',
        annotating: 'comparison',
        finalising: 'negotiation strategy',
      };
      setFailedStage(currentProgress ? stageNames[currentProgress.status] || currentProgress.status : null);

      let msg = err instanceof Error ? err.message : 'An error occurred while processing your document.';
      const lower = msg.toLowerCase();
      if (lower.includes('stall') || lower.includes('timed out') || lower.includes('504') || lower.includes('timeout')) {
        msg = 'Your document is large and processing took longer than expected. Large documents can take up to 15 minutes. Please try again -- retrying often succeeds.';
      }
      setErrorMessage(msg);
      setPhase('error');
    }
  };

  const handleRetryFromLastStage = () => {
    if (completedAnalysis) {
      handleProceed(completedAnalysis);
    }
  };

  const handleFullRetry = () => {
    setCompletedAnalysis(null);
    setPhase('upload');
    setErrorMessage('');
    setReviewProgress(null);
    setFailedStage(null);
  };

  if (phase === 'processing') {
    return (
      <ReviewProcessingLoader
        serviceType={serviceType}
        progress={reviewProgress}
      />
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4 animate-scale-in">
          <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-red-100">
            <AlertCircle className="w-9 h-9 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Something Went Wrong</h2>
          <p className="text-gray-600 mb-2 text-sm leading-relaxed">{errorMessage}</p>
          {failedStage && (
            <p className="text-gray-400 mb-8 text-xs">
              Failed during the {failedStage} stage
            </p>
          )}
          {!failedStage && <div className="mb-8" />}
          <div className="flex items-center justify-center gap-3">
            {completedAnalysis && failedStage && failedStage !== 'analysis' ? (
              <>
                <button
                  onClick={handleRetryFromLastStage}
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${info.gradient} text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-all shadow-lg`}
                >
                  <RotateCcw className="w-4 h-4" />
                  Retry from {failedStage}
                </button>
                <button
                  onClick={handleFullRetry}
                  className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Start Over
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleFullRetry}
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${info.gradient} text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-all shadow-lg`}
                >
                  <RotateCcw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={onBack}
                  className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Go Back
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const allDocsLoaded = isCommercial ? (documentText && documentTextB) : documentText;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!hideBackButton && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onBack}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors group"
            >
              <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
              Back
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className={`${isCommercial ? 'lg:col-span-3' : 'lg:col-span-3'}`}>
            <div className="mb-6 animate-fade-in-up">
              <div className="flex items-center justify-center lg:justify-start gap-2">
                {STEPS.map((step, i) => (
                  <React.Fragment key={step.label}>
                    <div className="flex items-center gap-2">
                      {step.status === 'complete' ? (
                        <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${info.gradient} flex items-center justify-center`}>
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      ) : step.status === 'active' ? (
                        <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${info.gradient} flex items-center justify-center ring-4 ring-gray-200`}>
                          <span className="text-white text-xs font-bold">{i + 1}</span>
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400 text-xs font-bold">{i + 1}</span>
                        </div>
                      )}
                      <span className={`text-xs font-medium hidden sm:inline ${
                        step.status === 'active' ? 'text-gray-900' : step.status === 'complete' ? 'text-gray-600' : 'text-gray-400'
                      }`}>{step.label}</span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`w-10 sm:w-16 h-px ${i === 0 ? `bg-gradient-to-r ${info.gradient}` : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-elevated animate-fade-in-up">
              <div className={`bg-gradient-to-r ${info.gradient} px-6 py-5`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                    {info.icon}
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-white">{info.title}</h1>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <CheckCircle className="w-3.5 h-3.5 text-white/80" />
                      <span className="text-white/80 text-xs">Payment confirmed</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {isCommercial ? (
                  <CommercialDualUpload
                    documentTextA={documentText}
                    fileNameA={fileName}
                    inputModeA={inputMode}
                    isDraggingA={isDragging}
                    fileInputRefA={fileInputRef}
                    onSetDocumentTextA={setDocumentText}
                    onSetFileNameA={setFileName}
                    onSetInputModeA={setInputMode}
                    onSetIsDraggingA={setIsDragging}
                    documentTextB={documentTextB}
                    fileNameB={fileNameB}
                    inputModeB={inputModeB}
                    isDraggingB={isDraggingB}
                    fileInputRefB={fileInputRefB}
                    onSetDocumentTextB={setDocumentTextB}
                    onSetFileNameB={setFileNameB}
                    onSetInputModeB={setInputModeB}
                    onSetIsDraggingB={setIsDraggingB}
                    onHandleFile={handleFile}
                    onHandleDrop={handleDrop}
                    gradient={info.gradient}
                  />
                ) : (
                  <TenancySingleUpload
                    documentText={documentText}
                    fileName={fileName}
                    inputMode={inputMode}
                    isDragging={isDragging}
                    fileInputRef={fileInputRef}
                    onSetDocumentText={setDocumentText}
                    onSetFileName={setFileName}
                    onSetInputMode={setInputMode}
                    onSetIsDragging={setIsDragging}
                    onHandleFile={(f) => handleFile(f, 'A')}
                    onHandleDrop={(e) => handleDrop(e, 'A')}
                    gradient={info.gradient}
                    isTenancy={isTenancy}
                  />
                )}

                {allDocsLoaded && (
                  <div className="space-y-5 animate-fade-in-up mt-6">
                    <div className="rounded-xl p-5 border bg-gray-50 border-gray-200">
                      <h4 className="text-sm font-semibold mb-4 text-gray-900">What We'll Do</h4>
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        {info.checklist.map((item, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-900" />
                            <span className="text-xs leading-relaxed text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => handleProceed()}
                      className={`group w-full py-4 rounded-xl text-white font-semibold text-sm bg-gradient-to-r ${info.gradient} hover:opacity-90 transition-all shadow-lg relative overflow-hidden`}
                    >
                      <span className="absolute inset-0 bg-shimmer animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span className="relative inline-flex items-center gap-2">
                        {isCommercial ? 'Proceed with Comparative Analysis' : 'Proceed with Analysis'}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </button>

                    <div className="flex items-center justify-center gap-4 py-3">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Lock className="w-3.5 h-3.5" />
                        <span>Encrypted transfer</span>
                      </div>
                      <div className="w-px h-3 bg-gray-200" />
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Shield className="w-3.5 h-3.5" />
                        <span>Data deleted after processing</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-2 animate-fade-in-up">
            <div className="sticky top-8 rounded-2xl overflow-hidden shadow-elevated-lg border border-gray-200">
              <div className="relative h-[540px]">
                <img
                  src={info.heroImage}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${info.overlayGradient}`} />
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                    {isTenancy ? <Home className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2 leading-tight">
                    {info.heroTagline}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {info.heroSubline}
                  </p>
                  <div className="mt-6 pt-6 border-t border-white/15">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 text-white/60 text-xs">
                        <Shield className="w-3.5 h-3.5" />
                        <span>Secure & Confidential</span>
                      </div>
                      <div className="w-px h-3 bg-white/20" />
                      <div className="flex items-center gap-1.5 text-white/60 text-xs">
                        <Lock className="w-3.5 h-3.5" />
                        <span>256-bit Encryption</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentUploadZone({
  label,
  helperText,
  documentText,
  fileName,
  inputMode,
  isDragging,
  fileInputRef,
  onSetDocumentText,
  onSetFileName,
  onSetInputMode,
  onSetIsDragging,
  onHandleFile,
  onHandleDrop,
  gradient,
}: {
  label: string;
  helperText: string;
  documentText: string;
  fileName: string | null;
  inputMode: 'file' | 'paste';
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onSetDocumentText: (t: string) => void;
  onSetFileName: (n: string | null) => void;
  onSetInputMode: (m: 'file' | 'paste') => void;
  onSetIsDragging: (d: boolean) => void;
  onHandleFile: (f: File) => void;
  onHandleDrop: (e: React.DragEvent) => void;
  gradient: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{helperText}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => { onSetInputMode('file'); onSetDocumentText(''); onSetFileName(null); }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
            inputMode === 'file'
              ? `bg-gradient-to-r ${gradient} text-white shadow-sm`
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Upload className="w-3.5 h-3.5" />
          Upload
        </button>
        <button
          onClick={() => { onSetInputMode('paste'); onSetDocumentText(''); onSetFileName(null); }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
            inputMode === 'paste'
              ? `bg-gradient-to-r ${gradient} text-white shadow-sm`
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <ClipboardPaste className="w-3.5 h-3.5" />
          Paste
        </button>
      </div>

      {inputMode === 'file' && !documentText && (
        <div
          onDragOver={(e) => { e.preventDefault(); onSetIsDragging(true); }}
          onDragLeave={() => onSetIsDragging(false)}
          onDrop={onHandleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
            isDragging
              ? 'border-gray-900 bg-gray-50 scale-[1.01]'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
          }`}
        >
          <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${
            isDragging ? `bg-gradient-to-br ${gradient}` : 'bg-gray-100'
          } transition-colors`}>
            <FileUp className={`w-6 h-6 ${isDragging ? 'text-white' : 'text-gray-400'} transition-colors`} />
          </div>
          <p className="text-gray-800 font-semibold text-sm mb-1">
            {isDragging ? 'Drop your file here' : 'Drop file or click to browse'}
          </p>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            {['PDF', 'DOC', 'DOCX', 'TXT'].map((fmt) => (
              <span key={fmt} className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium">
                {fmt}
              </span>
            ))}
            <span className="text-gray-300 text-[10px] ml-1">max 10MB</span>
          </div>
          <input
            ref={fileInputRef as React.RefObject<HTMLInputElement>}
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onHandleFile(file);
            }}
            className="hidden"
          />
        </div>
      )}

      {inputMode === 'paste' && !documentText && (
        <textarea
          placeholder="Paste the full text of the document here..."
          onChange={(e) => {
            if (e.target.value.trim()) {
              onSetDocumentText(e.target.value);
              onSetFileName('pasted-text.txt');
            }
          }}
          className="w-full h-40 p-3 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none placeholder:text-gray-400"
        />
      )}

      {documentText && (
        <div className="bg-gray-50 rounded-xl p-3.5 flex items-center justify-between border border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center`}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{fileName}</p>
              <p className="text-xs text-gray-500">{documentText.length.toLocaleString()} characters</p>
            </div>
          </div>
          <button
            onClick={() => { onSetDocumentText(''); onSetFileName(null); }}
            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      )}
    </div>
  );
}

function CommercialDualUpload({
  documentTextA, fileNameA, inputModeA, isDraggingA, fileInputRefA,
  onSetDocumentTextA, onSetFileNameA, onSetInputModeA, onSetIsDraggingA,
  documentTextB, fileNameB, inputModeB, isDraggingB, fileInputRefB,
  onSetDocumentTextB, onSetFileNameB, onSetInputModeB, onSetIsDraggingB,
  onHandleFile, onHandleDrop, gradient,
}: {
  documentTextA: string; fileNameA: string | null; inputModeA: 'file' | 'paste'; isDraggingA: boolean;
  fileInputRefA: React.RefObject<HTMLInputElement>;
  onSetDocumentTextA: (t: string) => void; onSetFileNameA: (n: string | null) => void;
  onSetInputModeA: (m: 'file' | 'paste') => void; onSetIsDraggingA: (d: boolean) => void;
  documentTextB: string; fileNameB: string | null; inputModeB: 'file' | 'paste'; isDraggingB: boolean;
  fileInputRefB: React.RefObject<HTMLInputElement>;
  onSetDocumentTextB: (t: string) => void; onSetFileNameB: (n: string | null) => void;
  onSetInputModeB: (m: 'file' | 'paste') => void; onSetIsDraggingB: (d: boolean) => void;
  onHandleFile: (f: File, target: 'A' | 'B') => void;
  onHandleDrop: (e: React.DragEvent, target: 'A' | 'B') => void;
  gradient: string;
}) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="relative">
        <div className="absolute -top-1 left-3 px-2 py-0.5 bg-white text-[10px] font-bold text-gray-700 uppercase tracking-wider z-10">
          Document A
        </div>
        <div className="border border-gray-300 rounded-xl p-4 pt-5 bg-gray-50/30">
          <DocumentUploadZone
            label="Original Lease"
            helperText="The original commercial lease, letter of intent, or initial draft"
            documentText={documentTextA}
            fileName={fileNameA}
            inputMode={inputModeA}
            isDragging={isDraggingA}
            fileInputRef={fileInputRefA}
            onSetDocumentText={onSetDocumentTextA}
            onSetFileName={onSetFileNameA}
            onSetInputMode={onSetInputModeA}
            onSetIsDragging={onSetIsDraggingA}
            onHandleFile={(f) => onHandleFile(f, 'A')}
            onHandleDrop={(e) => onHandleDrop(e, 'A')}
            gradient={gradient}
          />
        </div>
      </div>
      <div className="relative">
        <div className="absolute -top-1 left-3 px-2 py-0.5 bg-white text-[10px] font-bold text-gray-700 uppercase tracking-wider z-10">
          Document B
        </div>
        <div className="border border-gray-300 rounded-xl p-4 pt-5 bg-gray-50/30">
          <DocumentUploadZone
            label="Revised / Proposed Lease"
            helperText="The amended version, counterparty's redline, or latest draft"
            documentText={documentTextB}
            fileName={fileNameB}
            inputMode={inputModeB}
            isDragging={isDraggingB}
            fileInputRef={fileInputRefB}
            onSetDocumentText={onSetDocumentTextB}
            onSetFileName={onSetFileNameB}
            onSetInputMode={onSetInputModeB}
            onSetIsDragging={onSetIsDraggingB}
            onHandleFile={(f) => onHandleFile(f, 'B')}
            onHandleDrop={(e) => onHandleDrop(e, 'B')}
            gradient={gradient}
          />
        </div>
      </div>
    </div>
  );
}

function TenancySingleUpload({
  documentText, fileName, inputMode, isDragging, fileInputRef,
  onSetDocumentText, onSetFileName, onSetInputMode, onSetIsDragging,
  onHandleFile, onHandleDrop, gradient, isTenancy,
}: {
  documentText: string; fileName: string | null; inputMode: 'file' | 'paste'; isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onSetDocumentText: (t: string) => void; onSetFileName: (n: string | null) => void;
  onSetInputMode: (m: 'file' | 'paste') => void; onSetIsDragging: (d: boolean) => void;
  onHandleFile: (f: File) => void; onHandleDrop: (e: React.DragEvent) => void;
  gradient: string; isTenancy: boolean;
}) {
  return (
    <>
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => { onSetInputMode('file'); onSetDocumentText(''); onSetFileName(null); }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
            inputMode === 'file'
              ? `bg-gradient-to-r ${gradient} text-white shadow-sm`
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Upload className="w-4 h-4" />
          Upload File
        </button>
        <button
          onClick={() => { onSetInputMode('paste'); onSetDocumentText(''); onSetFileName(null); }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
            inputMode === 'paste'
              ? `bg-gradient-to-r ${gradient} text-white shadow-sm`
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <ClipboardPaste className="w-4 h-4" />
          Paste Text
        </button>
      </div>

      {inputMode === 'file' && !documentText && (
        <div
          onDragOver={(e) => { e.preventDefault(); onSetIsDragging(true); }}
          onDragLeave={() => onSetIsDragging(false)}
          onDrop={onHandleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-2xl p-10 sm:p-14 text-center cursor-pointer transition-all duration-300 ${
            isDragging
              ? 'border-gray-900 bg-gray-50 scale-[1.01]'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
          }`}
        >
          <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center ${
            isDragging ? `bg-gradient-to-br ${gradient}` : 'bg-gray-100'
          } transition-colors`}>
            <FileUp className={`w-7 h-7 ${isDragging ? 'text-white' : 'text-gray-400'} transition-colors`} />
          </div>
          <p className="text-gray-800 font-semibold mb-1.5">
            {isDragging ? 'Drop your file here' : 'Drop your file here or click to browse'}
          </p>
          <p className="text-gray-400 text-sm mb-4">Drag and drop or click to select</p>
          <div className="flex items-center justify-center gap-2">
            {['PDF', 'DOC', 'DOCX', 'TXT'].map((fmt) => (
              <span key={fmt} className="px-2.5 py-1 bg-gray-100 text-gray-500 rounded-lg text-xs font-medium">
                {fmt}
              </span>
            ))}
            <span className="text-gray-300 text-xs ml-1">max 10MB</span>
          </div>
          <input
            ref={fileInputRef as React.RefObject<HTMLInputElement>}
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onHandleFile(file);
            }}
            className="hidden"
          />
        </div>
      )}

      {inputMode === 'paste' && !documentText && (
        <textarea
          placeholder="Paste the full text of your agreement here..."
          onChange={(e) => {
            if (e.target.value.trim()) {
              onSetDocumentText(e.target.value);
              onSetFileName('pasted-text.txt');
            }
          }}
          className="w-full h-64 p-4 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none placeholder:text-gray-400"
        />
      )}

      {documentText && (
        <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between border border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{fileName}</p>
              <p className="text-xs text-gray-500">{documentText.length.toLocaleString()} characters loaded</p>
            </div>
          </div>
          <button
            onClick={() => { onSetDocumentText(''); onSetFileName(null); }}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      )}
    </>
  );
}
